import { DateTime } from "luxon"

/** updateSearchParamsWithFilters takes the output of `parseObservationFilters` and
 * updates the given URLSearchParams with all the values contained therein.
 * Note: For empty values in the filters object, it will remove any matching
 * values that are present in the URLSearchParams. This keeps the URL string "clean" for sharing / bookmarking.
 * On the other hand, this is an 'update' function because there might be other search params
 * present which has nothing to do with filtering - such as pagination, etc.
 */
export const updateSearchParamsWithFilters = (params, filters = {}) => {
  // Note: We sort to ensure a stable resulting string, such that it
  // is also useful e.g. in `useEffect` to know when filters have changed.
  Object.keys(filters)
    .sort()
    .forEach((p) => {
      const val = filters[p]
      if (Array.isArray(val) && val.length > 0) {
        params.set(p, val.join(","))
      } else if (!Array.isArray(val) && val != null && val != "") {
        params.set(p, val)
      } else {
        params.delete(p)
      }
    })
  // Idea: Functions in object keyed by relative offset, otherwise pass-through
  return params.toString()
}

/** If any of these keys are used as the value of `from` or `to` in filters,
 * produce an absolute date using these functions. */
export const dateTimeProducer = {
  // Current moment in time
  now: () => DateTime.local().toISO(),
  // 1 hour ago
  m1h: () => DateTime.local().minus({ hours: 1 }).toISO(),
  // 12 hours ago
  m12h: () => DateTime.local().minus({ hours: 12 }).toISO(),
  // 12 hours into the future
  p12h: () => DateTime.local().plus({ hours: 12 }).toISO(),
  // 24 hours ago
  m24h: () => DateTime.local().minus({ hours: 24 }).toISO(),
  // 24 hours into the future
  p24h: () => DateTime.local().plus({ hours: 24 }).toISO(),
  p48h: () => DateTime.local().plus({ hours: 48 }).toISO(),
  p7d: () => DateTime.local().plus({ days: 7 }).toISO(),
  p14d: () => DateTime.local().plus({ days: 14 }).toISO(),
  // 7 days ago
  m7d: () => DateTime.local().minus({ days: 7 }).toISO(),
  // 30 days ago
  m30d: () => DateTime.local().minus({ days: 30 }).toISO(),
  // Begin / end of day
  sd: () => DateTime.local().startOf("day").toISO(),
  ed: () => DateTime.local().endOf("day").toISO(),
  // Begin / end of week
  sw: () => DateTime.local().startOf("week").toISO(),
  ew: () => DateTime.local().endOf("week").toISO(),
  // Begin / end of month
  sm: () => DateTime.local().startOf("month").toISO(),
  em: () => DateTime.local().endOf("month").toISO(),
  // Begin / end of year
  sy: () => DateTime.local().startOf("year").toISO(),
  ey: () => DateTime.local().endOf("year").toISO(),
  // Yesterday
  syd: () => DateTime.local().minus({ days: 1 }).startOf("day").toISO(),
  eyd: () => DateTime.local().minus({ days: 1 }).endOf("day").toISO(),
  // Start of "3 days ago". This, together with `eyd` allows a "Previous 3 days" range
  sm3d: () => DateTime.local().minus({ days: 3 }).startOf("day").toISO(),
  // Last month
  slm: () => DateTime.local().minus({ months: 1 }).startOf("month").toISO(),
}

/** For a given date string, which could be an ISO string, or a key in `dateTimeProducer`,
 * the date will be expanded to concrete form, or returned unchanged if it does not
 * denote a producer time like `ew` (end of week).
 */
export const concretiseDate = (d) =>
  dateTimeProducer[d] != null ? dateTimeProducer[d]() : d

/** concretiseParams is used to make any relative parameters, like relative
 * date / time ranges, concrete, before sending to a server.
 */
export const concretiseParams = (params) => {
  // Expand known relative terms, e.g. relative times, to absolutes
  // Expand 'from' date with function if needed
  const from = params.get("from")
  if (dateTimeProducer[from] != null) {
    params.set("from", dateTimeProducer[from]())
  }
  // Expand 'to' date with function if needed
  const to = params.get("to")
  if (dateTimeProducer[to] != null) {
    params.set("to", dateTimeProducer[to]())
  }

  // Expand known relative terms, e.g. relative times, to absolutes
  // Expand 'start_date' date with function if needed
  const start_date = params.get("start_date")
  if (dateTimeProducer[start_date] != null) {
    params.set("start_date", dateTimeProducer[start_date]())
  }
  // Expand 'end_date' date with function if needed
  const end_date = params.get("end_date")
  if (dateTimeProducer[end_date] != null) {
    params.set("end_date", dateTimeProducer[end_date]())
  }
}

/** Given a "range" string like "< 5", "6 - 12", ">=10" or "17"
    returns a concrete, inclusive range specified as an array of
    two numbers in the form [lower, upper] where either could
    be undefined if no bound is implied.
    Returned array is never null, and always contains two elements
    of type (Number | undefined). */
export const asRangeInclusive = (rangeStr) => {
  let lower = undefined // Number | undefined
  let upper = undefined // Number | undefined
  // Whitespace-tolerant regex search for
  // [1] operator: "<", ">=", etc
  // [2] first number  (always present)
  // [4] second number (optional)
  const p = /\s*([<>]=?)?\s*(\d+)\s*(-\s*(\d+))?\s*/g
  const matches = Array.from(rangeStr.matchAll(p))
  if (matches.length > 0) {
    const operator = matches[0][1]
    const num1 = matches[0][2]
    const num2 = matches[0][4]

    // "5" or "3-9"
    if (!operator) {
      lower = parseInt(num1)
      upper = num2 ? parseInt(num2) : lower
    } else if (operator === "<=") {
      upper = parseInt(num1)
    } else if (operator === "<") {
      upper = parseInt(num1) - 1
    } else if (operator === ">=") {
      lower = parseInt(num1)
    } else if (operator === ">") {
      lower = parseInt(num1) + 1
    }
  }

  return [lower, upper]
}
